import { Alert, Box, CircularProgress, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomButton from "../Form/CustomButton";
import { CustomInput } from "../Form/CustomInputField";
import { ToastContainer } from "react-toastify";
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { getUserDetails, updatePasswordForCurrentUser } from "../../firebase/firebaseAuth";

const CARD_SX = {
  bgcolor: "#fff",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  p: 3,
  mb: 3,
  maxWidth: 600,
};

const SectionLabel = ({ children }) => (
  <Typography
    sx={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}
  >
    {children}
  </Typography>
);

const EMPTY_BILLING = { firstname: "", lastname: "", phone: "", address: "", city: "", state: "", country: "", zipcode: "" };
const EMPTY_SHIPPING = { shiPaddress: "", shiPcity: "", shiPstate: "", shiPcountry: "", shiPzipcode: "" };

function pickBilling(d) {
  return { firstname: d.firstname || "", lastname: d.lastname || "", phone: d.phone || "", address: d.address || "", city: d.city || "", state: d.state || "", country: d.country || "", zipcode: d.zipcode || "" };
}
function pickShipping(d) {
  return { shiPaddress: d.shiPaddress || "", shiPcity: d.shiPcity || "", shiPstate: d.shiPstate || "", shiPcountry: d.shiPcountry || "", shiPzipcode: d.shiPzipcode || "" };
}

export const Settings = () => {
  const cachedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [fetching, setFetching] = useState(true);
  const [profileData, setProfileData] = useState(cachedUser);

  // ── Password ──────────────────────────────────────────────────────────────
  const [oldPassword, setOldPassword] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [pwMsg, setPwMsg] = useState("");

  const handlePasswordSubmit = async () => {
    if (!oldPassword || !userPassword) return setPwMsg("Both fields are required");
    try {
      await updatePasswordForCurrentUser(oldPassword, userPassword);
      setPwMsg("Password updated!");
      setOldPassword("");
      setUserPassword("");
    } catch {
      setPwMsg("Error updating password. Check your current password.");
    }
  };

  // ── Billing address ───────────────────────────────────────────────────────
  const [billing, setBilling] = useState(pickBilling(cachedUser));

  // ── Shipping address ──────────────────────────────────────────────────────
  const [shipping, setShipping] = useState(pickShipping(cachedUser));

  // ── Fetch latest from Firestore on mount ──────────────────────────────────
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setFetching(false); return; }
    getUserDetails(uid)
      .then((data) => {
        if (!data) return;
        setProfileData(data);
        setBilling(pickBilling(data));
        setShipping(pickShipping(data));
        localStorage.setItem("user", JSON.stringify({ ...cachedUser, ...data }));
      })
      .catch(console.error)
      .finally(() => setFetching(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [addrMsg, setAddrMsg] = useState("");
  const [addrLoading, setAddrLoading] = useState(false);

  const bField = (key) => ({
    value: billing[key],
    onChange: (e) => { setBilling((p) => ({ ...p, [key]: e.target.value })); setAddrMsg(""); },
  });

  const sField = (key) => ({
    value: shipping[key],
    onChange: (e) => { setShipping((p) => ({ ...p, [key]: e.target.value })); setAddrMsg(""); },
  });

  const handleSaveAddress = async () => {
    setAddrLoading(true);
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");
      const payload = { ...billing, ...shipping };
      await updateDoc(doc(db, "users", uid), payload);
      localStorage.setItem("user", JSON.stringify({ ...cachedUser, ...profileData, ...payload }));
      setAddrMsg("Addresses saved!");
    } catch (e) {
      console.error(e);
      setAddrMsg("Error saving addresses.");
    } finally {
      setAddrLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  return (
    <>
      <ToastContainer />

      {/* ── Profile card ── */}
      <Box sx={CARD_SX}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 2 }}>Profile</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[
            { label: "Name", value: `${profileData?.firstname || ""} ${profileData?.lastname || ""}`.trim() || "—" },
            { label: "Email", value: profileData?.email || cachedUser?.email || "—" },
            { label: "Role", value: profileData?.role || "user" },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: "flex", gap: 2, alignItems: "baseline" }}>
              <Typography sx={{ fontSize: 12, color: "#6b7280", width: 52, flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#111827", textTransform: label === "Role" ? "capitalize" : "none" }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Address card ── */}
      <Box sx={CARD_SX}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 0.5 }}>Address</Typography>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 3 }}>
          Billing and shipping addresses used at checkout.
        </Typography>

        {addrMsg && (
          <Alert
            severity={addrMsg.startsWith("Addresses") ? "success" : "error"}
            onClose={() => setAddrMsg("")}
            sx={{ mb: 2.5, borderRadius: "8px" }}
          >
            {addrMsg}
          </Alert>
        )}

        {/* Billing */}
        <SectionLabel>Billing Address</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CustomInput label="First Name" className="w-full" {...bField("firstname")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomInput label="Last Name" className="w-full" {...bField("lastname")} />
            </Grid>
          </Grid>
          <CustomInput label="Phone" className="w-full" {...bField("phone")} />
          <CustomInput label="Street Address" className="w-full" {...bField("address")} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CustomInput label="City" className="w-full" {...bField("city")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomInput label="State / Province" className="w-full" {...bField("state")} />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CustomInput label="Country" className="w-full" {...bField("country")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomInput label="Zip / Postcode" className="w-full" {...bField("zipcode")} />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Shipping */}
        <SectionLabel>Shipping Address</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <CustomInput label="Street Address" className="w-full" {...sField("shiPaddress")} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CustomInput label="City" className="w-full" {...sField("shiPcity")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomInput label="State / Province" className="w-full" {...sField("shiPstate")} />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <CustomInput label="Country" className="w-full" {...sField("shiPcountry")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomInput label="Zip / Postcode" className="w-full" {...sField("shiPzipcode")} />
            </Grid>
          </Grid>
        </Box>

        <CustomButton
          sx={{ height: "44px", px: 4 }}
          color="secondary"
          className="text-sm !uppercase"
          onClick={handleSaveAddress}
          loading={addrLoading}
        >
          Save Addresses
        </CustomButton>
      </Box>

      {/* ── Change password card ── */}
      <Box sx={{ ...CARD_SX, mb: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 0.5 }}>Change Password</Typography>
        <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 3 }}>
          Update your account password below.
        </Typography>

        {pwMsg && (
          <Alert
            severity={pwMsg === "Password updated!" ? "success" : "error"}
            onClose={() => setPwMsg("")}
            sx={{ mb: 2, borderRadius: "8px" }}
          >
            {pwMsg}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomInput
            label="Current Password"
            type="password"
            className="w-full"
            onChange={(e) => { setOldPassword(e.target.value); setPwMsg(""); }}
            value={oldPassword}
            required
          />
          <CustomInput
            label="New Password"
            type="password"
            className="w-full"
            onChange={(e) => { setUserPassword(e.target.value); setPwMsg(""); }}
            value={userPassword}
            required
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <CustomButton
            sx={{ height: "44px", px: 4 }}
            color="secondary"
            className="text-sm !uppercase"
            onClick={handlePasswordSubmit}
          >
            Update Password
          </CustomButton>
        </Box>
      </Box>
    </>
  );
};
